<template>
  <form-edit-activity :form-type="FORM_TYPE" ref="formEditor"
                      :mode="mode"
                      :entity-id="entityId"
                      @form-created="formCreated"
                      :context="context"
                      :preset-fields="presetFields"
  >
  </form-edit-activity>
</template>

<script>
import Activity from "@/activity/Activity";
import PermissionsSet from "@/server/PermissionsSet";
import Rpc from "@/Rpc";
import FormsListActivity from "@/activity/templates/layouts/forms/FormsListActivity";
import FormsLoader from "@/components/forms/FormsLoader";

import FormEditActivity from "@/activity/templates/layouts/forms/FormEditActivity";
import FormMixin from "@/components/forms/customizations/mixins/FormMixin";
import ReachInput from "@/components/inputs/ReachInput";
import Product from "@/server/com/silkplm/model/plm/Product";
import DialogManager from "@/utils/DialogManager";
import _ from "lodash";
import {sortDynamicAttributes} from "@/components/cacheManager/DynamicAttributesSorter";

const FORM_TYPE = Product;

export default {
  name: "ProductsEditor",
  components: {ReachInput, FormMixin, FormEditActivity, FormsListActivity},
  permission: () => FormsLoader.formPermission(FORM_TYPE),

  props: {
    entityId: {default: null},
    mode: {type: String, default: "NEW"},
    context: {default: null},
    presetFields: {default: null},
  },

  created() {
  },

  async mounted() {
    this.form.onLoaded(async () => {
      let productCategoryAttributes = this.form.fields.productCategoryAttributes.value;
      if (productCategoryAttributes) {
        this.form.fields.productCategoryAttributes.value = await sortDynamicAttributes(productCategoryAttributes);
      }
    });
  },

  methods: {
    ...Activity.methods,

    async canClose(){
      return this.$refs.formEditor.canClose();
    },

    /** @type {FormModel} form */
    async formCreated(form) {
      this.form = form;
      this.form.validatedFieldByName('category').onChange(async (categoryId) => {
        if (this.mode === "NEW") {
          await this.syncCategories(categoryId);
        } else {
          if (!this.categoryIdFromOriginalSource) {
            this.categoryIdFromOriginalSource = this.form.originalSource.category;
          }
          if (this.form.fields['category'].value !== this.form.originalSource.category) {
            if (await DialogManager.yesNo($tr('categoryChangeWarning','Category change warning'),
                $tr("productsEditor.longMessage",
                    "After changing the category, please verify: </br> " +
                    "<b>Value Attributes</b> in Product (Info) </br>" +
                    " <b>Required Tests</b> in Product (Info) and Product in Season (Test Reports) </br>" +
                    " <b>Brandings Details</b> in Product in Season (Branding Settings) </br>" +
                    " <b>Key Visual field</b> in Product in Season (Branding Settings)</br>")
            )) {
              await this.syncCategories(categoryId);
            } else {
              this.form.fields['category'].value = this.categoryIdFromOriginalSource;
            }
          }
        }
      });
    },

    async syncCategories(categoryId) {
      await this.getCategoryAttributesToKeep();
      await this.getValueAttributes(categoryId);
    },

    customAdd(){
    },

    // getTitle(){
    //   let name = this.form?.fields?.name?.value;
    //   if(!name){
    //     return "";
    //   }
    //   return name;
    // },


    async getCategoryAttributesToKeep() {
      let uniqueAttributeNames = [];
      const scrollY = window.scrollY;
      for (let i = 0; i < this.form.fields.productCategoryAttributes.value?.length; i++) {
        if (this.form.fields.productCategoryAttributes.value[i].value) {
          let categoryAttribute = await this.form.fields.productCategoryAttributes.value[i].getCategoryAttributesIdEntity();
          let category = await categoryAttribute.getCategoryIdEntity();
          let productId = this.form.fields.id?.value;
          let valueAttributesFromCategory = [];
          if(!productId){
            valueAttributesFromCategory = await Rpc.plm.AttributesInCategory.getValueAttribute(category.id, null);
          }
          else{
            valueAttributesFromCategory = await Rpc.plm.AttributesInCategory.getValueAttribute(category.id, productId);
          }

          for (let valAttr of valueAttributesFromCategory) {
            if (valAttr.name === categoryAttribute.name) {
              let findIndex = _.findIndex(uniqueAttributeNames, function (name) {
                return name === valAttr.name;
              });
              if (findIndex === -1) {
                this.categoryAttributesToKeep.push(this.form.fields.productCategoryAttributes.value[i]);
                uniqueAttributeNames.push(valAttr.name);
              }
            }
          }
        }
      }
      this.$nextTick(() => {
        window.scrollTo(0, scrollY);
      });
    },

    async getValueAttributes(categoryId) {
      const scrollY = window.scrollY;
      let valueAttributesWithNameToKeep = [];

      for (let i = 0; i < this.categoryAttributesToKeep?.length; i++) {
        let categoryAttribute = await this.categoryAttributesToKeep[i].getCategoryAttributesIdEntity();
        valueAttributesWithNameToKeep[categoryAttribute.name] = this.categoryAttributesToKeep[i]
      }
      this.form.fields.productCategoryAttributes.value = [];

      let productId = this.form.fields.id?.value;
      let valueAttributesFromCategory = [];
      if(!productId){
        valueAttributesFromCategory = await Rpc.plm.AttributesInCategory.getValueAttribute(categoryId, null);
      }
      else{
        valueAttributesFromCategory = await Rpc.plm.AttributesInCategory.getValueAttribute(categoryId, productId);
      }

      if (valueAttributesFromCategory) {
        for (let valAttr of valueAttributesFromCategory) {
          if (valueAttributesWithNameToKeep) {
            for (let key in valueAttributesWithNameToKeep) {
              if (valAttr.name === key && (valAttr.valueAttribute.type === valueAttributesWithNameToKeep[key].type)) {
                if (!valAttr.valueAttribute.value) {
                  valAttr.valueAttribute.value = valueAttributesWithNameToKeep[key].value;
                }
              }
            }
          }
          this.form.fields.productCategoryAttributes.value.push(valAttr.valueAttribute);
        }
      }
      this.$nextTick(() => {
        window.scrollTo(0, scrollY);
      });
    },

    async openGeneratorEAN() {
      if (await DialogManager.yesNo($tr('assigningEAN','Assigning EAN'), this.message)) {
        await this.assignEAN();
      }
    },

    editEAN() {
      this.readOnly = false;
    },


    async assignEAN() {
      let res = await Rpc.plm.EANGenerator.allocateEAN().lock();
      if (res) {
        this.form.fields.EAN.value = res;
      } else {
        await DialogManager.info($tr('productsEditor.listOfFree.empty','The list of free EAN\'s is empty.'),
            $tr('productsEditor.ean.list.empty','EAN\'s list is empty - add them.'));
      }
    },

  },

  data: function () {
    return {
      FORM_TYPE,
      /** @type {FormModel} */
      form: null,
      readOnly: true,
      permissions: new PermissionsSet(),
      formConfig: FormsLoader.detectFormConfig(FORM_TYPE),
      formId: FormsLoader.detectFormConfig(FORM_TYPE).formId,
      message: "Are you sure you want to assign EAN? <br> Performing this operation will block this number.",
      categorySymbol: "",
      categoryAttributesToKeep: [],
      categoryIdFromOriginalSource: null,
    }
  }
}
</script>
